import { defineComponent as _defineComponent } from 'vue'
import SettingsForm from '@/components/layouts/SettingsForm.vue';
import showConfirmationDialog from '@/components/dialogs/confirmation-dialog';
import { useSettingsBreadcrumbs } from '@/mixins/settings-breadcrumbs';
import i18n from '@/plugins/i18n';
import {
  computed, getCurrentInstance, nextTick, onBeforeUnmount, onMounted, ref, watch,
} from 'vue';
import RichTextEditor from '@/components/editors/RichTextEditor.vue';
import ImageUploadDialog from '@/components/dialogs/ImageUploadDialog.vue';
import localized from '@/plugins/vue-localized-formatter/src/localized';
import {
  useSeatingTimeItems, useTimeInAdvanceItems, partysizes, useLanguages,
} from '@/components/views/settings/helpers/settings-values';
import ReservationService from '@/grpc-api/model/reservation-service';
import { loadStoreModule, unloadStoreModule } from '@/services/module-loader';
import ServiceManagerStore from '@/store/service-manager-store';
import { actionShowError, performDispatchAction, performSaveAction } from '@/services/vue-utils';
import { cloneModel, isModelEqualToModel } from '@/model/model-utils';
import { hostedWidgetURL } from '@/services/configuration';
import useUnsavedGuard from '@/composables/unsaved-guard';
import store from '@/store/store';
import { useRouter } from 'vue-router/composables';
import { ValidationObserver } from 'vee-validate';
import { dateFlooredToDate } from '@/services/time-utils';
import SettingsGroup from '@/components/layouts/SettingsGroup.vue';
import AmountField from '@/components/forms/AmountField.vue';
import GstplnApp from '@/components/apps/app';
import { fullSwatchPalette } from '@/components/helpers/colors';
import httpClient from '@/api/http-client';
import ImagePreviewField from '@/components/forms/ImagePreviewField.vue';
import toast from '@/plugins/toast-plugin/toast';
import copy from 'copy-to-clipboard';
import Tab from '@/model/Tab';
import ServiceManagerConditionalPanel from './ServiceManagerConditionalPanel.vue';


export default /*#__PURE__*/_defineComponent({
  __name: 'ServiceManagerEditor',
  setup(__props) {

const router = useRouter();

const serviceLoaded = ref(false);
const service = ref(new ReservationService());
const oldService = ref(service.value);

// Image Upload
const uploadDialog = ref(false);

const uploadImage = async (image: File | null) => {
  service.value.imageUrl = image ? URL.createObjectURL(image as Blob) : '';

  const ok = await performSaveAction(
    null,
    async () => {
      let imageUrl = '';

      // save image and get url, if undefined, image is being removed
      if (image !== null) {
        const key = store.state.settings.account.serviceManagerAccessKey ?? '';
        imageUrl = await httpClient.uploadImageFile(image, key);
      }

      service.value.imageUrl = imageUrl;
    },
  );

  if (ok) {
    uploadDialog.value = false;
  }
};

const weekDaysChanged = (value: number[]) => { value.sort(); service.value.weekDays = value; };

const saved = ref(false);

const isLoaded = computed(() => store.getters.isServiceManagerLoaded as boolean);

const isNew = computed(() => service.value.id <= 0 && isLoaded.value);

const isChanged = computed(() => !isModelEqualToModel(oldService.value, service.value) && !saved.value);
useUnsavedGuard(isChanged);

const tabs = computed(() => store.state.settings.tabs
  .filter((t) => t.usingWeekdaysAndTimes || service.value.tabs.some((st) => st.id === t.id)));

const widgetLink = computed(() => {
  const { defaultAccessKey: apiKey } = store.state.settings.account;
  const { locale, code: serviceCode } = oldService.value;
  if (!apiKey || !serviceCode) return undefined;
  return hostedWidgetURL({ apiKey, serviceCode, locale });
});

const fmtShortDate = (d: Date) => localized.veryShortDateText(d);

const clearButton = (val: any) => (val ? 'mdi-close' : undefined);

const fixedDurationSet = computed(() => !!service.value.duration);
const fixedDurationSetChanged = (val: boolean) => {
  if (!val) service.value.duration = undefined;
  else if (!service.value.duration) {
    const durations = service.value.tabs.map((t) => t.duration ?? 60);
    service.value.duration = Math.min(60, ...durations);
  }
};

const limitPeriodSet = computed(() => !!service.value.localStartDate && !!service.value.localEndDate);
const limitPeriodChanged = (val: boolean) => {
  if (!val) {
    service.value.localStartDate = undefined;
    service.value.localEndDate = undefined;
  } else {
    if (service.value.localStartDate === undefined) service.value.localStartDate = dateFlooredToDate(new Date());
    if (service.value.localEndDate === undefined) service.value.localEndDate = dateFlooredToDate(new Date());
  }
};

const blackoutSet = computed(() => service.value.blackouts.length > 0);
const blackoutSetChanged = (val: boolean) => {
  if (!val) {
    service.value.blackouts = [];
  } else {
    service.value.blackouts = [
      {
        blackoutStartDate: dateFlooredToDate(new Date()),
        blackoutEndDate: dateFlooredToDate(new Date()),
      },
    ];
  }
};

const blackoutStartDateChange = (i: number, date: Date) => {
  const blackout = { ...service.value.blackouts[i] };
  blackout.blackoutStartDate = date;
  service.value.blackouts.splice(i, 1, blackout);
};

const blackoutEndDateChange = (i: number, date: Date) => {
  const blackout = { ...service.value.blackouts[i] };
  blackout.blackoutEndDate = date;
  service.value.blackouts.splice(i, 1, blackout);
};

const delayedBlur = () => {
  setTimeout(() => (document.activeElement as HTMLElement)?.blur(), 100);
};

const addBlackoutRow = () => {
  delayedBlur();
  service.value.blackouts.push({
    blackoutStartDate: dateFlooredToDate(new Date()),
    blackoutEndDate: dateFlooredToDate(new Date()),
  });
};

const deleteBlackoutRow = (idx: number) => {
  delayedBlur();
  service.value.blackouts.splice(idx, 1);
};

const currencies = computed(() => store.state.serviceManager.currencies);
const stripeConfigured = computed(
  () => store.getters.gstplnApps.find((a: GstplnApp) => a.apiName === 'app.stripe')?.installed || false,
);
const stripeConnectConfigured = computed(
  () => store.getters.gstplnApps.find((a: GstplnApp) => a.apiName === 'app.stripe-connect')?.installed || false,
);

// eslint-disable-next-line vue/max-len
const configureStripe = () => router.replace(`/apps/manage/?category=payments&configure=stripe${stripeConnectConfigured.value || (!stripeConfigured.value && !stripeConnectConfigured.value) ? '-connect' : ''}`);

const stripeSet = computed(() => !!service.value.price || !!service.value.currency);
const stripeSetChanged = (val: boolean) => {
  if (!val) {
    service.value.price = undefined;
    service.value.isPricePerPerson = undefined;
    service.value.currency = undefined;
  } else {
    if (service.value.isPricePerPerson === undefined) service.value.isPricePerPerson = false;
    if (service.value.currency === undefined) [service.value.currency] = currencies.value;
  }
};

// section multiselect

const serviceTabsChanged = (value: Tab[]) => {
  service.value.tabs = Array.from(value);
  service.value.tabs.sort((t1, t2) => t1.id - t2.id);
};

const limitParallelSet = computed(() => !!service.value.maxParallelBookings);
const limitParallelSetChanged = (val: boolean) => {
  if (!val) {
    service.value.maxParallelBookings = undefined;
  } else if (service.value.maxParallelBookings === undefined) service.value.maxParallelBookings = 4;
};
const limitParallelItems = ref([...new Array(99)].map((e, i) => i + 1));

const reset = async () => {
  const route = router.currentRoute;

  const id = Number(route.params.id) || 0;
  let s = store.state.serviceManager.services.find((o) => o.id === id);

  if (!s) {
    s = new ReservationService();
    s.localStartTime = '06:00';
    s.localEndTime = '23:45';
    s.weekDays = [1, 2, 3, 4, 5, 6, 7];
    s.isActive = true;
    s.isPromoted = true;
    s.isDeposit = true;

    if (route.params.id !== 'new') {
      actionShowError(new Error(i18n.tc('error.service_not_found')));
      router.replace('/settings/service-manager/new');
    }
  }

  // when making duplicate of a service
  if (s && route.query.action === 'copy') {
    const dup = cloneModel(s);
    dup.id = 0;
    dup.title += ` (${i18n.t('label.copy_of')})`;

    oldService.value = new ReservationService();
    service.value = dup;
  } else {
    oldService.value = s;
    service.value = cloneModel(s);
  }

  serviceLoaded.value = true;
};

const load = async () => {
  if (!store.getters.isServiceManagerLoaded) {
    await performDispatchAction('loadServiceManager', {}, { streamLoad: true }) as ReservationService[] | null;
  }
  reset();
};

const refObserver = ref(null as null | InstanceType<typeof ValidationObserver>);
const save = async () => {
  service.value.weekDays.sort();
  service.value.title.trim();

  const ok = await performSaveAction(
    refObserver.value,
    async () => store.dispatch('serviceManagerUpdateService', { service: service.value }),
  );
  if (!ok) return;
  saved.value = true;
  router.replace('/settings/service-manager');
};

const deleteService = async () => {
  const s = cloneModel(service.value);
  s.isDeleted = true;
  const ok = await performSaveAction(
    undefined,
    async () => store.dispatch('serviceManagerUpdateService', { service: s }),
  );
  if (!ok) return;

  router.replace('/settings/service-manager');
};

const copySvcCode = () => {
  if (!service.value.code) { return; }
  copy(service.value.code);
  toast.success(i18n.t('message.service_id_copied'));
};

const deleteConfirmation = () => {
  showConfirmationDialog(
    i18n.tc('title.service_manager'),
    i18n.tc('message.confirm_delete_service'),
    { title: i18n.tc('button.yes'), action: () => deleteService() },
    { title: i18n.tc('button.cancel') },
  );
};

onBeforeUnmount(() => {
  unloadStoreModule(
    getCurrentInstance()?.proxy!,
    'serviceManager',
    async () => { store.dispatch('resetServiceManager'); },
  );
});

onMounted(() => {
  loadStoreModule(getCurrentInstance()?.proxy!, 'serviceManager', ServiceManagerStore, async () => { load(); });
});


return { __sfc: true,router, serviceLoaded, service, oldService, uploadDialog, uploadImage, weekDaysChanged, saved, isLoaded, isNew, isChanged, tabs, widgetLink, fmtShortDate, clearButton, fixedDurationSet, fixedDurationSetChanged, limitPeriodSet, limitPeriodChanged, blackoutSet, blackoutSetChanged, blackoutStartDateChange, blackoutEndDateChange, delayedBlur, addBlackoutRow, deleteBlackoutRow, currencies, stripeConfigured, stripeConnectConfigured, configureStripe, stripeSet, stripeSetChanged, serviceTabsChanged, limitParallelSet, limitParallelSetChanged, limitParallelItems, reset, load, refObserver, save, deleteService, copySvcCode, deleteConfirmation, SettingsForm, useSettingsBreadcrumbs, RichTextEditor, ImageUploadDialog, localized, useSeatingTimeItems, useTimeInAdvanceItems, partysizes, useLanguages, ValidationObserver, SettingsGroup, AmountField, fullSwatchPalette, ImagePreviewField, ServiceManagerConditionalPanel }
}

})